/**
 * ThemeContext — #2325 dark / light mode
 *
 * Precedence (highest → lowest):
 *   1. Explicit user choice (persisted to localStorage + DDB 2hives-users.theme_preference)
 *   2. OS prefers-color-scheme media query
 *   3. brand.css default = dark
 *
 * Usage:
 *   const { theme, resolvedTheme, setTheme } = useTheme();
 *   theme:         "dark" | "light" | "system"
 *   resolvedTheme: "dark" | "light"  (never "system")
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { adminFetch } from "../api/userApi";

export type ThemeChoice   = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

interface ThemeState {
  theme:         ThemeChoice;
  resolvedTheme: ResolvedTheme;
  setTheme:      (t: ThemeChoice) => void;
}

const LS_KEY = "2hives_theme";

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  if (resolved === "light") {
    root.setAttribute("data-theme", "light");
  } else {
    root.removeAttribute("data-theme");
  }
}

function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice === "system") return getSystemTheme();
  return choice;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>(() => {
    const stored = localStorage.getItem(LS_KEY) as ThemeChoice | null;
    if (stored === "dark" || stored === "light" || stored === "system") return stored;
    return "system";
  });

  const resolved = resolveTheme(theme);

  // Apply theme attribute whenever resolved theme changes
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Track OS preference changes when mode is "system"
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    function onchange() { applyTheme(resolveTheme("system")); }
    mq.addEventListener("change", onchange);
    return () => mq.removeEventListener("change", onchange);
  }, [theme]);

  const setTheme = useCallback((t: ThemeChoice) => {
    setThemeState(t);
    localStorage.setItem(LS_KEY, t);
    applyTheme(resolveTheme(t));
    // Persist to DDB 2hives-users.theme_preference — fire-and-forget
    adminFetch("PATCH", "/admin/user/me", { theme_preference: t }).catch(() => {
      // non-critical; local state is already correct
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme: resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}

/**
 * ThemeHydrator — renders nothing; syncs the server-persisted theme_preference
 * into ThemeContext once the user profile loads. Mount inside AuthProvider +
 * ThemeProvider (both must be ancestors).
 */
export function ThemeHydrator({ serverTheme }: { serverTheme: string | null | undefined }) {
  const { theme, setTheme } = useTheme();
  // Only hydrate once on first valid server value; don't override if the user
  // already changed the theme in this session (localStorage takes precedence).
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    if (!serverTheme) return;
    const valid = ["dark", "light", "system"] as ThemeChoice[];
    const t = serverTheme as ThemeChoice;
    if (valid.includes(t) && t !== theme) {
      setTheme(t);
    }
    hydrated.current = true;
  }, [serverTheme, theme, setTheme]);

  return null;
}
